<template>
  <v-dialog v-model="dialog" persistent max-width="700px">
    <v-card dark class="pb-4">
      <v-stepper v-model="step" class="elevation-0">
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1">
            Kundendaten
            <small>Informationen prüfen</small>
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 2" step="2">
            Pakete auswählen
            <small>Upgrade wählen</small>
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 3" step="3">
            Bestätigung
            <small>Unterschreiben</small>
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="4">
            Bezahlung
            <small>Abschliessen</small>
          </v-stepper-step>
        </v-stepper-header>
      </v-stepper>

      <v-card-title class="text-h5 primary white--text pt-4 pb-4">
        <v-icon dark left>mdi-star-plus-outline</v-icon>
        Zusatzpakete Upgrade
      </v-card-title>

      <v-window v-model="step" touchless>
        <v-window-item :value="1">
          <v-container class="pa-6">
            <div v-if="!customer.selection || !customer.contractCreator">
              <v-row>
                <v-col cols="12">
                  <user-search
                    :value="customer.contractCreator"
                    :disabled="viewOnly"
                    ignoreRights
                    instructor
                    label="Visum (Vertragersteller)"
                    outlined
                    @update="updateCreator"></user-search>
                </v-col>
                <v-col cols="12">
                  <user-search
                    :value="customer.selection"
                    :disabled="viewOnly || customer.contractCreator === null"
                    ignoreRights
                    label="Kunde"
                    outlined
                    @update="updateCustomer"></user-search>
                </v-col>
              </v-row>
            </div>
            <div v-else>
              <div class="text-center mb-4" v-if="!useCustomer">
                <v-btn v-if="!viewOnly && customer.infos" color="grey" variant="text" @click="resetCustomer">
                  <v-icon left>mdi-account-refresh-outline</v-icon>
                  Kunde ändern
                </v-btn>
                <v-progress-circular v-else color="primary" indeterminate></v-progress-circular>
              </div>

              <v-row v-if="customer.infos">
                <v-col cols="12">
                  <v-select
                    :disabled="viewOnly"
                    hide-details
                    label="Anrede"
                    :items="[
                      { name: 'Herr', value: 'm' },
                      { name: 'Frau', value: 'w' },
                    ]"
                    item-value="value"
                    item-text="name"
                    v-model="customer.infos.gender"
                    outlined
                    dense></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field hide-details :disabled="viewOnly" label="Vorname" v-model="customer.infos.firstName" outlined dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field hide-details :disabled="viewOnly" label="Nachname" v-model="customer.infos.lastName" outlined dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    hide-details
                    :disabled="viewOnly"
                    label="Geburtstag"
                    type="date"
                    v-model="customer.infos.birthday"
                    outlined
                    dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field hide-details :disabled="viewOnly" label="Telefon" v-model="customer.infos.phone" outlined dense></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    hide-details
                    :disabled="viewOnly"
                    label="E-Mail"
                    type="email"
                    v-model="customer.infos.email"
                    outlined
                    dense></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field hide-details :disabled="viewOnly" label="Strasse" v-model="customer.infos.street" outlined dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    hide-details
                    :disabled="viewOnly"
                    type="number"
                    label="Postleitzahl"
                    v-model="customer.infos.zip"
                    outlined
                    dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field hide-details :disabled="viewOnly" label="Stadt" v-model="customer.infos.city" outlined dense></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-container>
        </v-window-item>

        <v-window-item :value="2">
          <v-container class="pa-6">
            <v-row v-if="fetchingPossibilities">
              <v-col cols="12" class="text-center pa-10">
                <v-progress-circular size="64" color="primary" indeterminate></v-progress-circular>
                <p class="mt-4 text-grey">Lade verfügbare Pakete...</p>
              </v-col>
            </v-row>
            <v-row v-else-if="possibilities">
              <v-col cols="12">
                <v-alert v-if="!showExternalPackages" type="info">
                  {{ possibilities.roodyMembershipName }} bis {{ $moment(possibilities.endDate).format("DD.MM.YYYY") }}
                </v-alert>
                <v-alert v-else type="info" color="primary">Zusatzpakete aus anderen Studios</v-alert>
              </v-col>

              <v-col cols="12">
                <v-row>
                  <v-col cols="12" md="6" v-for="(pck, index) in packages.filter((e) => true)" :key="index">
                    <v-card
                      class="package-card fill-height d-flex align-center"
                      :class="{ 'package-card--selected': selectedPackages.includes(pck) }"
                      elevation="2"
                      @click="togglePackage(pck)">
                      <v-icon class="package-card__check-icon" color="white">mdi-check-circle</v-icon>
                      <div class="special-offer-chip" v-if="pck.packageName.includes('mescan')">NEU</div>

                      <v-card-text>
                        <v-row align="center" no-gutters>
                          <v-col>
                            <div class="text-h6 package-title">
                              <span v-if="pck.amountSelectable && pck.amount">{{ pck.amount }}x </span>
                              {{ pck.packageName }}
                            </div>
                            <div class="package-subtitle" v-if="pck.amountSelectable && pck.amount">
                              {{ displayCurrency(pck.amount * parseInt(pck.price)) }}
                              <span
                                class="text-decoration-line-through ml-2"
                                v-if="
                                  pck.usualPrice &&
                                  (pck.calculateWithEndDate ||
                                    (pck.calculateWithEndDateSixMonths && parseInt(pck.price) !== parseInt(pck.usualPrice)))
                                ">
                                {{ displayCurrency(pck.amount * pck.usualPrice) }}
                              </span>
                            </div>
                            <div class="package-subtitle" v-else>
                              {{ displayCurrency(pck.price) }}
                              <span
                                class="text-decoration-line-through ml-2"
                                v-if="
                                  pck.usualPrice &&
                                  (pck.calculateWithEndDate ||
                                    (pck.calculateWithEndDateSixMonths && parseInt(pck.price) !== parseInt(pck.usualPrice)))
                                ">
                                {{ displayCurrency(pck.usualPrice) }}
                              </span>
                            </div>
                          </v-col>
                          <v-col cols="auto">
                            <v-tooltip bottom v-if="showExternalPackages && pck.availableInStudios">
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon v-on="on" v-bind="attrs" @click.stop>
                                  <v-icon>mdi-map-marker-outline</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ pck.availableInStudios.join(", ") }}</span>
                            </v-tooltip>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>

              <v-col v-if="possibilities.packages && possibilities.packages.length > 0" cols="12">
                <v-alert type="success" icon="mdi-layers-triple-outline">
                  <b>Vorhandene Zusatzpakete:</b> {{ possibilities.packages.join(", ") }}
                </v-alert>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12">
                <v-alert type="error" border="start">
                  Keine Mitgliedschaft oder keine verfügbaren Zusatzpakete gefunden. Stimmt die E-Mail im TAC überein?
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
        </v-window-item>

        <v-window-item :value="3">
          <v-container class="pa-6">
            <upgrade-contract-package-table
              :selectedPackages="selectedPackages"
              :total="displayCurrency(totalPackagePrice)"
              v-if="!checkbox"></upgrade-contract-package-table>

            <v-alert v-if="salePackages.length > 0 && !checkbox" class="mt-5 mx-auto" max-width="500px" type="info">
              {{ salePackages.length === 1 ? "Das Aktionspaket" : "Die Aktionspakete" }}
              <b>{{ salePackages.map((e) => e.packageName).join(", ") }}</b>
              {{ salePackages.length === 1 ? "erneuert" : "erneuern" }} sich zum Regulärpreis.
            </v-alert>

            <v-alert class="mt-5" icon="mdi-gavel" type="warning" outlined v-if="!checkbox">
              Dieser Vertrag verlängert sich automatisch um jeweils 12 Monate, wenn dieser nicht 45 Tage vor Vertragsablauf schriftlich gekündigt
              wird. Massgebend für das Einhalten der Frist ist das Datum des Poststempels. Der Mitgliedschaftspreis für den verlängerten Vertrag
              richtet sich nach der, 60 Tage vor Verlängerungsbeginn, geltenden Mitgliedschaftspreisliste (die Preisliste liegt immer in jedem Center
              der well come FIT zur Einsicht auf).
            </v-alert>

            <v-row class="mt-6">
              <v-col class="justify-center d-flex" cols="12">
                <v-checkbox v-model="checkbox">
                  <template v-slot:label>
                    <div style="max-width: 500px; text-align: left">
                      Ich bestätige hiermit, dass ich meinen aktuellen Mitgliedschaftsvertrag erfolgreich mit den ausgewählten Paketen aktualisiert
                      habe und dem Upgrade zustimme.
                    </div>
                  </template>
                </v-checkbox>
              </v-col>

              <div v-if="checkbox" class="w-100">
                <v-col align="center" cols="12">
                  <v-card-subtitle class="text-h6">Unterschrift Kunde</v-card-subtitle>
                  <signature-pad
                    :signature-data="signature"
                    @startDrawing="signatureFound = true"
                    @resetDrawing="signatureFound = false"
                    :whiteBg="true"
                    @saveSignature="saveSignature"
                    ref="signatureConditions"></signature-pad>
                </v-col>
                <v-col v-if="customer.isUnderage" class="px-5 mt-6" cols="12">
                  <v-divider class="mb-6"></v-divider>
                  <v-card-subtitle class="text-h6 text-center">Gesetzlicher Vertreter</v-card-subtitle>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field hide-details v-model="customer.legalRepresentative.firstName" label="Vorname" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field hide-details v-model="customer.legalRepresentative.lastName" label="Nachname" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field hide-details v-model="customer.legalRepresentative.email" label="E-Mail" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field hide-details v-model="customer.legalRepresentative.address" label="Adresse" outlined dense></v-text-field>
                    </v-col>
                  </v-row>
                  <signature-pad
                    class="mt-4"
                    :whiteBg="true"
                    :signature-data="signatureLegalRepresentative"
                    @saveSignature="saveSignatureLR"
                    ref="signatureLRConditions"></signature-pad>
                </v-col>
              </div>
            </v-row>
          </v-container>
        </v-window-item>

        <v-window-item :value="4">
          <v-container class="pa-6">
            <upgrade-contract-package-table
              :selectedPackages="selectedPackages"
              :total="displayCurrency(totalPackagePrice)"></upgrade-contract-package-table>

            <v-alert v-if="salePackages.length > 0" class="mt-5 mx-auto" max-width="500px" type="info">
              {{ salePackages.length === 1 ? "Das Aktionspaket" : "Die Aktionspakete" }}
              <b>{{ salePackages.map((e) => e.packageName).join(", ") }}</b>
              {{ salePackages.length === 1 ? "erneuert" : "erneuern" }} sich zum Regulärpreis von
              <b>{{
                displayCurrency(salePackages.reduce((acc, e) => acc + (e.amountSelectable && e.amount ? e.amount * e.usualPrice : e.usualPrice), 0))
              }}</b
              >.
            </v-alert>

            <v-card-text class="text-center text-h6 mt-6">Wie möchtest Du bezahlen?</v-card-text>

            <div v-if="!useCustomer">
              <v-row justify="center">
                <v-col cols="auto">
                  <v-card
                    link
                    class="payment-card"
                    :class="{ 'payment-card--selected': payment === 'Kartenzahlung' }"
                    @click="payment = 'Kartenzahlung'">
                    <v-card-text class="text-center">
                      <v-icon size="48">mdi-credit-card-outline</v-icon>
                      <div class="mt-2 font-weight-bold">Kartenzahlung</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="auto">
                  <v-card link class="payment-card" :class="{ 'payment-card--selected': payment === 'Bar' }" @click="payment = 'Bar'">
                    <v-card-text class="text-center">
                      <v-icon size="48">mdi-cash-multiple</v-icon>
                      <div class="mt-2 font-weight-bold">Bar</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
              <v-row justify="center" class="mt-4">
                <v-col cols="12" md="8">
                  <v-textarea label="Notiz (optional)" v-model="paymentComment" outlined rows="2"></v-textarea>
                </v-col>
              </v-row>
            </div>
            <div v-else>
              <v-row justify="center">
                <v-col cols="auto">
                  <v-card link class="payment-card" :class="{ 'payment-card--selected': payment === 'Rechnung' }" @click="payment = 'Rechnung'">
                    <v-card-text class="text-center">
                      <v-icon size="48">mdi-receipt-text-outline</v-icon>
                      <div class="mt-2 font-weight-bold">Rechnung</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </v-container>
        </v-window-item>
      </v-window>

      <v-card-actions class="pa-4">
        <v-btn size="large" @click="step === 1 ? cancel() : preStep()">
          {{ step === 1 ? "Abbrechen" : "Zurück" }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          size="large"
          color="primary"
          variant="flat"
          :disabled="
            !customer.selection ||
            !customer.contractCreator ||
            (customer.selection && !customer.infos) ||
            fetchingPossibilities ||
            (step === 2 && !selectedPackages.length > 0 && !(step === 2 && !showExternalPackages)) ||
            (step === 3 && !checkbox) ||
            (step === 4 && !payment)
          "
          :loading="confirming || updatingUserData"
          @click="step === 1 ? updateUserData() : step !== 4 ? nextStep() : confirm()">
          {{ step !== 4 ? "Weiter" : "Bestätigen & Abschliessen" }}
          <v-icon right>{{ step !== 4 ? "mdi-arrow-right" : "mdi-check" }}</v-icon>
        </v-btn>
      </v-card-actions>

      <v-dialog persistent max-width="300px" v-model="showPackageAmountSelect">
        <v-card v-if="packageAmountSelect">
          <v-card-title class="white--text primary">
            <span class="mx-auto">Anzahl {{ packageAmountSelect.packageName }}</span>
          </v-card-title>
          <v-card-text align="center" class="pt-4">
            <span>Wähle die Anzahl aus</span>
            <v-row class="mt-5" align="center" justify="center">
              <v-btn color="red" class="white--text" rounded-xl @click="decrement">
                <v-icon>mdi-minus</v-icon>
              </v-btn>
              <span style="font-size: 16px; font-weight: bold" class="mx-4">
                {{ packageAmountSelect.amount }}
              </span>
              <v-btn color="primary" rounded-xl @click="increment">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-btn class="mx-auto" text @click="setPackageAmount">Bestätigen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
    <v-dialog v-model="infoDialog" max-width="500px" persistent>
      <v-card dark>
        <v-card-title class="text-h5">
          <v-icon left color="primary">mdi-information-outline</v-icon>
          {{ infoDialogTitle }}
        </v-card-title>
        <v-card-text class="pt-4 white--text" style="font-size: 1rem; line-height: 1.6">
          {{ infoDialogText }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="infoDialog = false"> Verstanden </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
// DEIN BESTEHENDER SCRIPT-BLOCK BLEIBT HIER UNVERÄNDERT
import userSearch from "@/components/userSearch.vue"
import signaturePad from "@/components/signaturePad/signaturePad.vue"
import { roundToNearestFiveCents, roundToNearestFiveCentsWithoutFormatting } from "@/moduls/currency/currency.js"
import upgradeContractPackageTable from "./upgradeContractPackageTable.vue"
import api from "@/api"
export default {
  components: { userSearch, signaturePad, upgradeContractPackageTable },
  props: {
    dialog: {
      default: false,
      required: true,
    },
    useCustomer: {
      default: false,
      required: true,
    },
  },
  data() {
    return {
      infoDialog: false, // Steuert die Sichtbarkeit des neuen Dialogs
      infoDialogTitle: "",
      infoDialogText: "",
      updatingUserData: false,
      showExternalPackages: false,
      confirming: false,
      payment: null,
      signature: null,
      signatureLegalRepresentative: null,
      signatureFound: false,
      checkbox: false,
      packageAmountSelect: null,
      selectedPackages: [],
      showPossibilities: false,
      possibilities: null,
      fetchingPossibilities: false,
      viewOnly: false,
      paymentComment: "",
      step: 1,
      customer1: {
        isUnderage: false,
        contractCreator: "5f55fdad6716c132adf93baa",
        contractCreatorInfos: {
          firstName: "Dom",
          lastName: "Ammann",
          email: "dominic.ammann.ch@gmail.com",
        },
        selection: "63a98151c8c2993f5c6a6f72",
        legalRepresentative: {
          firstName: null,
          lastName: null,
          email: null,
          address: null,
          firstSignature: null,
          secondSignature: null,
        },
        infos: {
          gender: "m",
          firstName: "Marcel",
          lastName: "Ammann",
          email: "marcel.ammann@axacom.ch",
          phone: "+41763852899",
          birthday: "1999-12-28",
          street: "Hauptstrasse 138",
          city: "Tägerwilen",
          zip: "8274",
        },
      },
      customer: {
        isUnderage: false,
        contractCreator: null,
        contractCreatorInfos: null,
        selection: null,
        legalRepresentative: null,
        infos: {
          gender: null,
          firstName: null,
          lastName: null,
          birthday: null,
          phone: null,
          email: null,
          street: null,
          zip: null,
          city: null,
        },
      },
    }
  },
  async created() {
    this.initCustomer()
  },
  watch: {},
  computed: {
    salePackages() {
      return this.selectedPackages.filter(
        (s) => s.usualPrice && (s.calculateWithEndDate || (s.calculateWithEndDateSixMonths && parseInt(s.price) !== parseInt(s.usualPrice)))
      )
    },
    packages() {
      let pack = []
      if (this.showExternalPackages) {
        if (!this.possibilities) return pack
        pack = this.possibilities.externalPackages || []
      } else {
        if (!this.possibilities) return pack
        pack = this.possibilities.availablePackages || []
      }
      if (this.customer.isUnderage) pack = pack.filter((e) => !e.hideForMinors)
      return pack
    },
    studio() {
      return this.possibilities.studioPopulated || { code: null, name: null }
    },

    totalPackagePrice() {
      let price = 0
      this.selectedPackages.forEach((e) => {
        let amount = e.amountSelectable && e.amount ? e.amount : 1
        let priceLocal = parseFloat(e.price) * amount
        price += roundToNearestFiveCentsWithoutFormatting(priceLocal)
      })
      return price
    },
    showPackageAmountSelect() {
      return this.packageAmountSelect !== null
    },
  },
  methods: {
    async initCustomer() {
      if (this.useCustomer) {
        this.payment = "Rechnung"
        let user = await api.getUser()
        if (user && user.user) {
          let userInfos = user.user
          this.customer.contractCreator = userInfos._id
          let contractCreatorInfos = {
            firstName: userInfos.vorname,
            lastName: userInfos.nachname,
            email: userInfos.email,
          }

          this.customer.contractCreatorInfos = contractCreatorInfos

          this.customer.selection = userInfos._id

          let customerEntry = {
            gender: userInfos.geschlecht,
            firstName: userInfos.vorname,
            lastName: userInfos.nachname,
            email: userInfos.email,
            phone: userInfos.telefon,
            birthday: userInfos.geburtstag,
            street: userInfos.adresse_1,
            city: userInfos.stadt,
            zip: userInfos.plz,
          }

          this.resetLegalRepresentative()

          this.customer.infos = customerEntry

          if (!this.checkIfBirthdayIsValid(this.customer.infos.birthday)) {
            this.$toast.error("Bitte gib ein gültiges Geburtsdatum ein")
          } else {
            this.checkIfUnderage()
          }
        }
      }
    },
    cancel() {
      this.updatingUserData = false
      this.$emit("hide")
      this.checkbox = false
      this.selectedPackages = []
      this.step = 1
      this.signature = null
      this.signatureLegalRepresentative = null
      this.customer = {
        isUnderage: false,
        contractCreator: null,
        contractCreatorInfos: null,
        selection: null,
        legalRepresentative: {
          firstName: null,
          lastName: null,
          email: null,
          address: null,
          firstSignature: null,
          secondSignature: null,
        },
        infos: {
          gender: null,
          firstName: null,
          lastName: null,
          birthday: null,
          phone: null,
          email: null,
          street: null,
          zip: null,
          city: null,
        },
      }
      this.initCustomer()
    },
    async confirm() {
      this.confirming = true
      let contractInfo = {
        paymentComment: this.paymentComment,
        possibilities: this.possibilities,
        studio: this.possibilities.studio,
        customer: this.customer,
        signature: this.signature,
        signatureLegalRepresentative: this.signatureLegalRepresentative,
        packages: this.selectedPackages,
        payment: this.payment,
        checkbox: this.checkbox,
        frontendPrice: this.totalPackagePrice,
      }
      try {
        let status = await api.createContractUpgrade({ contractInfo, useCustomer: this.useCustomer })
        console.log(status)
        this.$toast.success("Upgrade erstellt")
        this.cancel()
        this.$emit("created")
      } catch (e) {
        this.$toast.error("Fehler beim Erstellen, bitte versuche es erneut")
      }
      this.confirming = false
    },
    saveSignature(signature) {
      this.signature = signature
    },
    saveSignatureLR(signature) {
      this.signatureLegalRepresentative = signature
    },
    increment() {
      if (this.packageAmountSelect.amount < 10) {
        this.packageAmountSelect.amount++
      }
      this.$forceUpdate()
    },
    decrement() {
      if (this.packageAmountSelect.amount > 1) {
        this.packageAmountSelect.amount--
      }
      this.$forceUpdate()
    },
    preStep() {
      if (this.step === 2 && this.showExternalPackages) {
        this.showExternalPackages = false
        return
      }
      if (this.step !== 1) {
        this.step--
      }
    },
    nextStep() {
      if (this.step === 2) {
        this.checkIfUnderage()
      }

      if (this.step === 2 && !this.showExternalPackages && this.possibilities.externalPackages && this.possibilities.externalPackages.length > 0) {
        this.showExternalPackages = true
        return
      }
      if (this.step === 3) {
        if (!this.signatureFound) {
          this.$toast.error("Bitte unterschreibe das Upgrade")
          return
        }
        this.$refs.signatureConditions.saveSignature()
        if (this.customer.isUnderage) {
          this.$refs.signatureLRConditions.saveSignature()
        }
        console.log("saving signatures")
      }
      this.step++
    },
    setPackageAmount() {
      this.packageAmountSelect = null
    },
    togglePackage(pck) {
      if (pck.packageName.toLowerCase().includes("anteil") && pck.packageName.toLowerCase().includes("pelvi") && this.useCustomer) {
        // Setzt den Text und Titel für den Dialog und zeigt ihn an
        this.infoDialogTitle = "Nur vor Ort buchbar"
        this.infoDialogText =
          "Das Paket 'Pelvipower-Stuhl anteilsmässig' kann nur direkt bei uns im Studio gelöst werden. Bitte frage einfach einen unserer Trainer, sie helfen dir gerne weiter!"
        this.infoDialog = true
        return // Bricht die weitere Ausführung ab
      }
      if (pck.packageName.toLowerCase().includes("anteil") && pck.packageName.toLowerCase().includes("pelvi") && !this.useCustomer) {
        // Setzt den Text und Titel für den Dialog und zeigt ihn an
        this.infoDialogTitle = "Neuer Vertrag erforderlich"
        this.infoDialogText =
          "Kunde muss sofort einen neuen Vertrag für die nächste Laufzeit (12 Monate) unterzeichnen. (Mit dem Zusatzpaket Pelvipower-Stuhl)"
        this.infoDialog = true
      }
      this.packageAmountSelect = null
      const index = this.selectedPackages.indexOf(pck)
      if (index > -1) {
        if (pck.amountSelectable) pck.amount = 0
        this.selectedPackages.splice(index, 1)
        if (
          this.studio.code === "stgalleneinstein" &&
          pck.packageName === "Wellness/Spa plus" &&
          this.selectedPackages.map((e) => e.packageName).includes("Hydrojet")
        ) {
          this.selectedPackages = this.selectedPackages.filter((e) => e.packageName !== "Hydrojet")
        }
      } else {
        if (
          this.studio.code === "stgalleneinstein" &&
          pck.packageName === "Hydrojet" &&
          !(
            this.selectedPackages.map((e) => e.packageName).includes("Wellness/Spa plus") || this.possibilities.packages.includes("Wellness/Spa plus")
          )
        ) {
          alert("Nur mit Wellness/Spa plus")
        } else {
          if (pck.amountSelectable) {
            this.packageAmountSelect = pck
            this.packageAmountSelect.amount = 1
          }
          this.selectedPackages.push(pck)
        }
      }
    },
    displayCurrency(amount) {
      return roundToNearestFiveCents(amount)
    },

    async updateUserData() {
      if (!this.checkIfBirthdayIsValid(this.customer.infos.birthday)) {
        this.$toast.error("Bitte gib ein gültiges Geburtsdatum ein")
        return
      }
      this.updatingUserData = true
      try {
        let status = await api.editUserProfileFromContract({
          data: {
            infos: this.customer.infos,
            userId: this.customer.selection,
          },
        })

        if (!status.success && status.message) this.$toast.error(status.message)
        this.updatingUserData = false
        if (!status.success) return false
      } catch (e) {
        this.$toast.error("Fehler beim aktualisieren des Profils, bitte versuche es erneut")
        this.updatingUserData = false
        return false
      }
      this.updatingUserData = false
      this.fetchUpgradePossibilities()
    },
    async fetchUpgradePossibilities() {
      this.checkIfUnderage()
      this.selectedPackages = []
      this.step = 2

      this.fetchingPossibilities = true
      let customerId = this.customer.selection
      try {
        let infos = await api.fetchPackageUpgradePossibilitesTemp(customerId, false)
        this.possibilities = infos
      } catch (e) {
        this.$toast.error("Fehler beim laden der Möglichkeiten")
        console.log(e)
      }
      this.fetchingPossibilities = false
    },
    logthis() {
      console.log(this.customer)
    },
    resetCustomer() {
      this.customer.infos = null
      this.resetLegalRepresentative()
      this.customer.isUnderage = false
      this.customer.selection = null
    },
    checkIfUnderage() {
      let birthday = this.customer.infos.birthday
      let age = this.$moment().diff(birthday, "years")

      if (age < 18) {
        this.customer.isUnderage = true
      } else {
        this.customer.isUnderage = false
      }

      this.$forceUpdate()
    },
    resetLegalRepresentative() {
      this.customer.legalRepresentative = {
        firstName: null,
        lastName: null,
        email: null,
        address: null,
        firstSignature: null,
        secondSignature: null,
      }
    },
    async updateCreator(id) {
      this.customer.contractCreator = id
      let contractCreator = await api.getUserForManager(id)
      let contractCreatorInfos = {
        firstName: contractCreator.vorname,
        lastName: contractCreator.nachname,
        email: contractCreator.email,
      }

      this.customer.contractCreatorInfos = contractCreatorInfos

      this.$forceUpdate()
    },
    checkIfBirthdayIsValid(birthday) {
      return this.$moment(birthday, "YYYY-MM-DD", true).isValid()
    },
    async updateCustomer(id) {
      this.customer.selection = id
      let customer = await api.getUserForManager(id)
      let customerEntry = {
        gender: customer.geschlecht,
        firstName: customer.vorname,
        lastName: customer.nachname,
        email: customer.email,
        phone: customer.telefon,
        birthday: customer.geburtstag,
        street: customer.adresse_1,
        city: customer.stadt,
        zip: customer.plz,
      }

      this.resetLegalRepresentative()

      this.customer.infos = customerEntry

      if (!this.checkIfBirthdayIsValid(this.customer.infos.birthday)) {
        this.$toast.error("Bitte gib ein gültiges Geburtsdatum ein")
      } else {
        this.checkIfUnderage()
      }

      this.$forceUpdate()
    },
  },
}
</script>

<style lang="scss" scoped>
// HIER KOMMT DER NEUE STYLE-BLOCK
.package-card {
  position: relative;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  .package-title {
    font-weight: bold;
    line-height: 1.4;
  }

  .package-subtitle {
    font-size: 1rem;
    color: #4caf50; // Ein schönes Grün für den Preis
    font-weight: 500;
  }

  .package-subtitle .text-decoration-line-through {
    color: #f44336; // Rot für den alten Preis
    font-size: 0.8rem;
  }

  .package-card__check-icon {
    position: absolute;
    top: 12px;
    right: 12px;
    opacity: 0;
    transform: scale(0.8);
    transition: all 0.2s ease-in-out;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1) !important;
  }

  &--selected {
    background: var(--v-primary-lighten-4); // Eine hellere Variante der Primärfarbe
    border: 2px solid var(--v-primary);
    color: var(--v-primary-darken-3);

    .package-card__check-icon {
      opacity: 1;
      transform: scale(1);
    }

    // Hintergrund für das Check-Icon, damit es sichtbar ist
    &::before {
      content: "";
      position: absolute;
      top: -2px;
      right: -2px;
      width: 48px;
      height: 48px;
      background: var(--v-primary);
      border-bottom-left-radius: 20px;
      border-top-right-radius: 10px;
      z-index: 0;
    }
  }
}

.payment-card {
  width: 150px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1) !important;
  }

  &--selected {
    border: 2px solid var(--v-primary);
    background: var(--v-primary-lighten-5);
    color: var(--v-primary-darken-3);
  }
}

.special-offer-chip {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: var(--v-primary);
  color: white;
  font-size: 12px;
  font-weight: bold;
  width: auto;
  padding: 2px 10px;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

// Stellt sicher, dass der Dialog-Titel über dem Stepper bleibt
::v-deep .v-card__title {
  position: sticky;
  top: 0;
  z-index: 2;
}
</style>
